import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { can } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Agenda from "@/pages/Agenda";
import Patients from "@/pages/Patients";
import PatientDetail from "@/pages/PatientDetail";
import Physios from "@/pages/Physios";
import Medici from "@/pages/Medici";
import Gym from "@/pages/Gym";
import Machines from "@/pages/Machines";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import Cassa from "@/pages/Cassa";
import Documents from "@/pages/Documents";
import Reports from "@/pages/Reports";
import Backup from "@/pages/Backup";
import Settings from "@/pages/Settings";
import Updates from "@/pages/Updates";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-loading">
        <div className="w-10 h-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function Guard({ section, roles, children }) {
  const { user } = useAuth();
  if (roles && !roles.includes(user?.role)) return <Navigate to="/" replace />;
  if (section && user?.effective_permissions && !can(user, section)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<RequireAuth><Layout /></RequireAuth>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/calendario" element={<Guard section="calendario"><CalendarPage /></Guard>} />
              <Route path="/agenda" element={<Guard section="agenda"><Agenda /></Guard>} />
              <Route path="/pazienti" element={<Guard section="pazienti"><Patients /></Guard>} />
              <Route path="/pazienti/:id" element={<Guard section="pazienti"><PatientDetail /></Guard>} />
              <Route path="/medici" element={<Guard section="medici"><Medici /></Guard>} />
              <Route path="/fisioterapisti" element={<Guard section="fisioterapisti"><Physios /></Guard>} />
              <Route path="/palestra" element={<Guard section="checkin"><Gym /></Guard>} />
              <Route path="/macchinari" element={<Guard section="macchinari"><Machines /></Guard>} />
              <Route path="/pagamenti" element={<Guard section="pagamenti"><Payments /></Guard>} />
              <Route path="/spese" element={<Guard section="spese"><Expenses /></Guard>} />
              <Route path="/cassa" element={<Guard section="cassa"><Cassa /></Guard>} />
              <Route path="/documenti" element={<Guard section="documenti"><Documents /></Guard>} />
              <Route path="/report" element={<Guard section="report"><Reports /></Guard>} />
              <Route path="/backup" element={<Guard section="backup" roles={["admin", "segreteria"]}><Backup /></Guard>} />
              <Route path="/impostazioni" element={<Guard section="impostazioni"><Settings /></Guard>} />
              <Route path="/aggiornamenti" element={<Guard roles={["admin", "titolare", "segreteria", "coordinatore"]}><Updates /></Guard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
